import React, { useState, useMemo, useEffect } from "react";
import Select from "react-select";
import escapeRegExp from "lodash/escapeRegExp";
import { isEmpty } from "lodash";

const MAX_DISPLAYED_OPTIONS = 500;

const ClientFilteredMeasures = ({
  allMeasures,
  clientMeasuresMap,
  field,
  selectedClient = "",
  placeholder = "Select measures...",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);

  useEffect(() => {
    setSelectedValues([]);
    const el = document.getElementById(field);
    if (el) el.value = "";
  }, [selectedClient, field]);

  const missingMapping = !!selectedClient && !clientMeasuresMap[selectedClient];

  const availableOptions = useMemo(() => {
    if (!selectedClient) return allMeasures;
    if (clientMeasuresMap[selectedClient]) {
      const clientMeasureNames = new Set(clientMeasuresMap[selectedClient]);
      return allMeasures.filter((m) => clientMeasureNames.has(m.value));
    }
    return [];
  }, [selectedClient, allMeasures, clientMeasuresMap]);

  const filteredOptions = useMemo(() => {
    if (!inputValue) {
      return [...availableOptions].sort((a, b) =>
        a.label.toUpperCase().localeCompare(b.label.toUpperCase())
      );
    }

    const matchByStart = [];
    const matchByInclusion = [];
    const regByInclusion = new RegExp(escapeRegExp(inputValue), "i");
    const regByStart = new RegExp(`^${escapeRegExp(inputValue)}`, "i");

    for (const option of availableOptions) {
      if (regByInclusion.test(option.label)) {
        if (regByStart.test(option.label)) {
          matchByStart.push(option);
        } else {
          matchByInclusion.push(option);
        }
      }
    }
    return [...matchByStart, ...matchByInclusion];
  }, [inputValue, availableOptions]);

  const slicedOptions = useMemo(
    () => filteredOptions.slice(0, MAX_DISPLAYED_OPTIONS),
    [filteredOptions]
  );

  const updateHiddenField = (values) => {
    const el = document.getElementById(field);
    if (el) {
      el.value = values.length > 0 ? JSON.stringify(values) : "";
    }
  };

  const onChange = (opts) => {
    if (isEmpty(opts)) {
      setSelectedValues([]);
      updateHiddenField([]);
      return;
    }
    const values = opts.map((opt) => opt.value);
    setSelectedValues(opts);
    updateHiddenField(values);
  };

  return (
    <>
      {missingMapping && (
        <p className="text-sm text-amber-600 mb-1">
          No measure mapping found for this client. Measures are unavailable until mapping is configured.
        </p>
      )}
      <Select
        isClearable
        isMulti
        options={slicedOptions}
        value={selectedValues}
        onChange={onChange}
        onInputChange={(value) => setInputValue(value)}
        filterOption={() => true}
        placeholder={missingMapping ? "No measures available for this client" : placeholder}
        isDisabled={missingMapping}
      />
    </>
  );
};

export default ClientFilteredMeasures;
